import { Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import {
  Appointment,
  AppointmentsGroupBy,
  DotFitter,
  Fitter,
  Score,
  TimeFilters,
} from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  DotFittersSandboxService,
  FittersSandboxService,
  SandboxService,
  ScoresSandboxService,
} from '@sc/data-access-state-management';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AppointmentSubParams,
  FilterFunc,
  FitterSortStrategy,
  InputObservables,
  PrimaryFilters,
} from '../types';
import { AppointmentsSubService } from './appointment-sub.service';

/**
 * NOTE - In order to have different appointment subscription services across different components,
 * this service builder should be put in the list of providers of each component that uses it
 */
@Injectable({
  providedIn: 'root',
})
export class AppointmentSubscriptionBuilder {
  private hasCustomSortStrategy = false;

  private readonly primaryFilters: PrimaryFilters = {
    appointmentsFilter: (input) => input,
    fittersFilter: (input) => input,
    dotFittersFilter: (input) => input,
    scoresFilter: (input) => input,
  };

  private readonly inputObs: InputObservables = {
    appointmentMap: of('fitters'),
    inCenter: of(true),
    atHome: of(true),
    replacementVehicle: of(false),
    timeFilter: of(null),
    generalists: of(false),
    specialist: of(true),
    sortStrategy: of('score-dist'),
  };

  private readonly parameters: AppointmentSubParams = {
    checkCutOffs: true,
    checkMobility: true,
    checkReplacementVehicle: true,
    removeDuplicates: true,
    fetchSlotsTimeout: 20000,
    maxCachedMobileCoords: 20,
    maxCachedMobileApmtByCoord: 200,
  };

  constructor(
    private readonly sandboxService: SandboxService,
    private readonly appointmentsSB: AppointmentsSandboxService,
    private readonly fittersSB: FittersSandboxService,
    private readonly dotFittersSB: DotFittersSandboxService,
    private readonly scoresSB: ScoresSandboxService,
    private readonly dossierSB: DossierSandboxService,
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly configService: ConfigurationService,
    private readonly logger: InsightService
  ) {}

  addAppointmentsFilter(predicate: FilterFunc<Appointment>) {
    this.primaryFilters.appointmentsFilter = predicate;
    return this;
  }

  addFittersFilter(predicate: FilterFunc<Fitter>) {
    this.primaryFilters.fittersFilter = predicate;
    return this;
  }

  addDotFittersFilter(predicate: FilterFunc<DotFitter>) {
    this.primaryFilters.dotFittersFilter = predicate;
    return this;
  }

  addScoresFilter(predicate: FilterFunc<Score>) {
    this.primaryFilters.scoresFilter = predicate;
    return this;
  }

  addAppointmentsMapObservable(obs: Observable<AppointmentsGroupBy>) {
    this.inputObs.appointmentMap = obs;
    if (!this.hasCustomSortStrategy) {
      this.inputObs.sortStrategy = obs.pipe(
        map((groupBy) => (groupBy === 'days' ? 'score-dist' : 'score-dist')) // score-dist always by Default for now
      );
    }
    return this;
  }

  addInCenterObservable(obs: Observable<boolean>) {
    this.inputObs.inCenter = obs;
    return this;
  }

  addAtHomeObservable(obs: Observable<boolean>) {
    this.inputObs.atHome = obs;
    return this;
  }

  addReplacementVehicleObservable(obs: Observable<boolean>) {
    this.inputObs.replacementVehicle = obs;
    return this;
  }

  addGeneralistFittersObservable(obs: Observable<boolean>) {
    this.inputObs.generalists = obs;
    return this;
  }

  addSpecialistFittersObservable(obs: Observable<boolean>) {
    this.inputObs.specialist = obs;
    return this;
  }

  addTimeFilterObservable(obs: Observable<TimeFilters | null>) {
    this.inputObs.timeFilter = obs;
    return this;
  }

  addSortStrategyObservable(obs: Observable<FitterSortStrategy>) {
    this.inputObs.sortStrategy = obs;
    this.hasCustomSortStrategy = true;
    return this;
  }

  checkCutOffFeature(doCheck = true) {
    this.parameters.checkCutOffs = doCheck;
    return this;
  }

  checkMobilityService(doCheck = true) {
    this.parameters.checkMobility = doCheck;
    return this;
  }

  checkReplacementVehicleService(doCheck = true) {
    this.parameters.checkReplacementVehicle = doCheck;
    return this;
  }

  removeDuplicates(doRemove = true) {
    this.parameters.removeDuplicates = doRemove;
    return this;
  }

  fetchTimeout(newTimeout: number) {
    this.parameters.fetchSlotsTimeout = newTimeout;
    return this;
  }

  maxCachedMobileCoords(newMaxCachedMobileCoords: number) {
    this.parameters.maxCachedMobileCoords = newMaxCachedMobileCoords;
    return this;
  }

  maxCachedMobileApmtByCoord(newMaxCachedMobileApmtByCoord: number) {
    this.parameters.maxCachedMobileApmtByCoord = newMaxCachedMobileApmtByCoord;
    return this;
  }

  build(): AppointmentsSubService {
    return new AppointmentsSubService(
      this.sandboxService,
      this.appointmentsSB,
      this.fittersSB,
      this.dotFittersSB,
      this.scoresSB,
      this.dossierSB,
      this.featureFlipService,
      this.configService,
      this.logger,
      this.primaryFilters,
      this.inputObs,
      this.parameters
    );
  }
}
